import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import ReceiptIcon from "@mui/icons-material/Receipt";
import { useForm } from "react-hook-form";
import { CustomListToolbar } from "../../../ListToolbar/CustomListToolbar";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "@/store";
import {
  fetchPaymentsBySection,
  createPayment,
  updatePayment,
  deletePayment,
} from "@/store/paymentSlice";
import {
  fetchPaymentOptionManagement,
  upsertPaymentOptionManagement,
} from "@/store/paymentOptionManagementSlice";
import {
  fetchBudgetCategories,
  createBudgetCategory,
} from "@/store/budgetSlice";
import {
  CustomButton,
  CustomModal,
  CustomTable,
  MobileTable,
  CustomColumnDef,
  CustomRenderCellWithDash,
  CustomSingleSelect,
  CustomSelect,
  colors,
  type CategoryOption,
} from "@ippon/shared-components";
import { useGlobalToast } from "@/hooks/useGlobalToast";
import { useCan } from "@/hooks/useCan";
import { normalizeArray } from "@/utils/propertySection";
import PaymentDrawer, {
  type PaymentDrawerFormValues,
  type SectionPaymentItemOption,
  DEFAULT_STATUSES,
} from "../PaymentDrawer";
import PaymentDetailDrawer from "../PaymentDetailDrawer";
import { usePreviewDrawerNav } from "../PreviewDrawerNavContext";
import dayjs from "dayjs";

interface PaymentHistoryTabProps {
  propertySectionId: string;
  propertyId: string;
  /** パンくず構築用 物件名 */
  propertyName?: string;
  /** パンくず構築用 Unit 名 (landmark_name 由来) */
  unitName?: string;
  sectionData?: any;
  /**
   * Unit の収益区分に対応する上位カテゴリ。入金内訳プルダウンの科目絞り込みに使う。
   * 省略時は sectionData.revenue_type (明細レベル) から導出する。
   */
  revenueGroup?: "stock_rent" | "stock_other" | "other";
}

const PaymentHistoryTab: React.FC<PaymentHistoryTabProps> = ({
  propertySectionId,
  propertyId,
  propertyName,
  unitName,
  sectionData,
  revenueGroup: revenueGroupProp,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { showToast } = useGlobalToast();
  const can = useCan();
  const payments = useSelector((s: RootState) => (s as any).payments.list.data);
  const loading = useSelector(
    (s: RootState) => (s as any).payments.list.loading,
  );
  const availableStatuses = useSelector(
    (s: RootState) => (s as any).payments.list.availableStatuses as string[],
  );
  const availableItems = useSelector(
    (s: RootState) => (s as any).payments.list.availableItems as string[],
  );
  const optionRecord = useSelector(
    (s: RootState) => (s as any).paymentOptionManagement.record,
  );

  const [statusOptions, setStatusOptions] =
    useState<CategoryOption[]>(DEFAULT_STATUSES);

  // 新規登録: 既存 PaymentDrawer (CustomModal の registerModal) を維持
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [createOpen, setCreateOpen] = useState(false);
  // 編集: PaymentDetailDrawer (PreviewDrawer + /payments/:id ページ埋め込み) に切替
  const [editPaymentId, setEditPaymentId] = useState<string | null>(null);
  // 区画プレビュードロワー内なら 2重ドロワーを避け、同一ドロワー内で入金詳細に切り替える。
  const drawerNav = usePreviewDrawerNav();
  const openPaymentDetail = (paymentId: string) => {
    if (drawerNav) {
      drawerNav.openPaymentDetail({
        paymentId,
        propertySectionId,
        propertyId,
        propertyName,
        unitName,
        onUpdated: () =>
          dispatch(
            fetchPaymentsBySection({
              propertySectionId,
              status: appliedFilters.status || undefined,
              payment_item: appliedFilters.payment_item || undefined,
            }),
          ),
      });
    } else {
      setEditPaymentId(paymentId);
    }
  };
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [perPage, setPerPage] = useState<number>(50);
  const [currentPage, setCurrentPage] = useState(1);

  // 適用済みフィルタ (検索ボタン押下で更新)
  const [appliedFilters, setAppliedFilters] = useState<{
    status: string;
    payment_item: string;
  }>({ status: "", payment_item: "" });

  const { control, getValues, setValue } = useForm<{
    perPage: number;
    status_filter: string;
    payment_item_filter: string;
  }>({
    defaultValues: {
      perPage: 50,
      status_filter: "",
      payment_item_filter: "",
    },
  });

  const perPageOptions = useMemo(
    () => [
      { value: 10, label: "10件" },
      { value: 25, label: "25件" },
      { value: 50, label: "50件" },
      { value: 100, label: "100件" },
    ],
    [],
  );

  const budgetCategories = useSelector(
    (s: RootState) => (s as any).budgets?.categories?.data || [],
  );

  useEffect(() => {
    // 入金内訳の選択肢は BudgetCategory (収入) を起点にする
    dispatch(fetchBudgetCategories() as any);
  }, [dispatch]);

  // この Unit (PropertySection) の収入科目の上位カテゴリ (revenue_group)。
  // 入金内訳のプルダウンは Unit.revenue_type に対応する revenue_group の科目だけを出す (§4.2)。
  //   - 明示 prop revenueGroup を優先
  //   - 無ければ sectionData.revenue_type (明細レベル: rent/ancillary_income/other_income) から導出
  //     rent → stock_rent (貸室) / ancillary_income・other_income → stock_other (その他収入)
  const revenueGroup: "stock_rent" | "stock_other" | "other" | undefined =
    useMemo(() => {
      if (
        revenueGroupProp === "stock_rent" ||
        revenueGroupProp === "stock_other" ||
        revenueGroupProp === "other"
      ) {
        return revenueGroupProp;
      }
      const rt = sectionData?.revenue_type;
      if (rt === "rent") return "stock_rent";
      if (rt === "ancillary_income" || rt === "other_income") {
        return "stock_other";
      }
      return undefined;
    }, [revenueGroupProp, sectionData]);

  const sectionPaymentItems: SectionPaymentItemOption[] = useMemo(() => {
    if (!Array.isArray(budgetCategories) || budgetCategories.length === 0)
      return [];

    // 契約 / セクションから既定金額を引っ張るための名前マップ (貸室科目のみ既定値あり)
    const contracts = normalizeArray(sectionData?.contractor_details);
    const latest: Record<string, any> = contracts[contracts.length - 1] ?? {};
    const defaultByName: Record<string, number> = {
      賃料: Number(latest.monthly_rent || sectionData?.monthly_rent || 0),
      共益費: Number(
        latest.maintenance_fee || sectionData?.maintenance_fee || 0,
      ),
      敷金: Number(
        latest.security_deposit ||
          sectionData?.security_deposit_before_amortization ||
          sectionData?.security_deposit_before ||
          sectionData?.security_deposit ||
          0,
      ),
      礼金: Number(latest.key_money || sectionData?.key_money || 0),
    };

    // 明細フリー: 収益カテゴリで絞らず全収入科目を出す (新規登録のカスタム収益カテゴリも表示)。
    // 各科目の revenue_group をラベルに (固定3つは変換、カスタムは値=名称)。
    const rgLabel = (rg: string): string =>
      rg === "stock_rent"
        ? "貸室"
        : rg === "stock_other"
          ? "付帯"
          : rg === "other"
            ? "その他"
            : rg;
    return (
      (budgetCategories as any[])
        .filter((c) => c && c.category_type === "income" && !c.deleted_at)
        .slice()
        // display_order 昇順。PaymentDrawer 側の収益カテゴリ安定ソートと合わせ、
        // 並び替え (display_order の即時 PATCH) がグループ内順に反映される。
        .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0))
        .map((c) => ({
          category_id: c.category_id,
          name: c.name,
          defaultAmount: defaultByName[c.name] ?? 0,
          categoryLabel: rgLabel(String(c.revenue_group || "other")),
        }))
    );
  }, [budgetCategories, sectionData]);

  useEffect(() => {
    dispatch(fetchPaymentOptionManagement())
      .unwrap()
      .then((rec: any) => {
        if (!rec) return;
        if (Array.isArray(rec.statuses)) {
          const merged = Array.from(
            new Set([...DEFAULT_STATUSES.map((o) => o.value), ...rec.statuses]),
          );
          setStatusOptions(
            merged.map((v) => ({ label: String(v), value: String(v) })),
          );
        }
      })
      .catch(() => {});
  }, [dispatch]);

  useEffect(() => {
    if (!optionRecord) return;
    if (Array.isArray(optionRecord.statuses)) {
      const merged = Array.from(
        new Set([
          ...DEFAULT_STATUSES.map((o) => o.value),
          ...optionRecord.statuses,
        ]),
      );
      setStatusOptions(
        merged.map((v) => ({ label: String(v), value: String(v) })),
      );
    }
  }, [optionRecord]);

  useEffect(() => {
    if (propertySectionId) {
      dispatch(
        fetchPaymentsBySection({
          propertySectionId,
          status: appliedFilters.status || undefined,
          payment_item: appliedFilters.payment_item || undefined,
        }),
      );
    }
  }, [
    dispatch,
    propertySectionId,
    currentPage,
    perPage,
    appliedFilters.status,
    appliedFilters.payment_item,
  ]);

  // フィルタ用 Select のオプション (バックエンドから取得した実在の値)
  // 未選択 = 絞り込みなし (placeholder 表示)
  const statusFilterOptions = useMemo(
    () => availableStatuses.map((s) => ({ label: s, value: s })),
    [availableStatuses],
  );

  const paymentItemFilterOptions = useMemo(
    () => availableItems.map((i) => ({ label: i, value: i })),
    [availableItems],
  );

  const handleSearch = useCallback(() => {
    const statusVal = getValues("status_filter");
    const itemVal = getValues("payment_item_filter");
    const normalize = (v: any): string =>
      typeof v === "string" ? v : (v?.value ?? "");
    setAppliedFilters({
      status: normalize(statusVal),
      payment_item: normalize(itemVal),
    });
    setCurrentPage(1);
  }, [getValues]);

  const handleClear = useCallback(() => {
    setValue("status_filter", "");
    setValue("payment_item_filter", "");
    setAppliedFilters({ status: "", payment_item: "" });
    setCurrentPage(1);
  }, [setValue]);

  // ステータスの手動追加は一旦保留 (収入モーダルと同じく固定の選択肢のみ)。
  // 追加 UI は PaymentDrawer に onAddNewStatus を渡さないことで無効化している。

  // 入金保存時に、ローカル新規追加された科目 (isNew) を BudgetCategory として永続化する。
  // この Unit の revenue_group (無ければ「その他」) の収入科目を作成し category_id を返す。
  // (グループセレクトと同じ「ローカル追加 → 親保存時にまとめて永続化」パターン)
  const persistNewCategory = useCallback(
    async (name: string): Promise<string> => {
      const created: any = await dispatch(
        createBudgetCategory({
          name: name.trim(),
          category_type: "income",
          revenue_group: revenueGroup ?? "other",
        }) as any,
      ).unwrap();
      return created.category_id as string;
    },
    [dispatch, revenueGroup],
  );

  // 明細セレクト「新規登録」モーダル: 収益カテゴリ + 明細項目 を即時作成 → 一覧再取得 → category_id。
  const handleAddCategory = useCallback(
    async (name: string, rg: string): Promise<string | undefined> => {
      const created: any = await dispatch(
        createBudgetCategory({
          name: name.trim(),
          category_type: "income",
          revenue_group: rg as "stock_rent" | "stock_other" | "other",
        }) as any,
      ).unwrap();
      await dispatch(fetchBudgetCategories() as any);
      return created?.category_id as string | undefined;
    },
    [dispatch],
  );

  // 収益カテゴリ選択肢 (新規登録モーダル用): 固定3つ + 既存カスタム distinct。
  const revenueGroupOptions = useMemo(() => {
    const base = [
      { value: "stock_rent", label: "貸室" },
      { value: "stock_other", label: "付帯" },
      { value: "other", label: "その他" },
    ];
    const seen = new Set(base.map((b) => b.value));
    for (const c of budgetCategories as any[]) {
      const rg = c?.category_type === "income" ? c.revenue_group : undefined;
      if (rg && !seen.has(rg)) {
        seen.add(rg);
        base.push({ value: rg, label: rg });
      }
    }
    return base;
  }, [budgetCategories]);

  const [statusMap, setStatusMap] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!Array.isArray(payments)) return;
    const next: Record<string, string> = {};
    payments.forEach((p: any) => {
      next[p.payment_id] = p.status || "";
    });
    setStatusMap(next);
  }, [payments]);

  const handleInlineStatusChange = useCallback(
    async (paymentId: string, sortKey: string, newStatus: string) => {
      const previous = statusMap[paymentId];
      setStatusMap((prev) => ({ ...prev, [paymentId]: newStatus }));
      try {
        const updateBody: any = {
          propertySectionId,
          paymentId,
          sort_key: sortKey,
          status: newStatus,
        };
        if (newStatus === "入金済") {
          updateBody.transfer_datetime = new Date().toISOString();
        }
        await dispatch(updatePayment(updateBody)).unwrap();
        // 入金更新成功 → 適用したステータスが DEFAULT_STATUSES でも optionRecord でも
        // 未登録なら、ここで初めてサーバに永続化する (ローカル追加分の遅延 upsert)。
        const isDefault = DEFAULT_STATUSES.some((d) => d.value === newStatus);
        const beStatuses: string[] = Array.isArray(optionRecord?.statuses)
          ? optionRecord.statuses
          : [];
        if (!isDefault && !beStatuses.includes(newStatus)) {
          dispatch(
            upsertPaymentOptionManagement({
              existingId: optionRecord?.payment_option_management_id,
              statuses: [...beStatuses, newStatus],
              payment_items: optionRecord?.payment_items || [],
            }),
          ).catch(() => {});
        }
        showToast({ type: "success", message: "ステータスを更新しました" });
      } catch {
        setStatusMap((prev) => ({ ...prev, [paymentId]: previous }));
        showToast({ type: "error", message: "ステータスの更新に失敗しました" });
      }
    },
    [statusMap, dispatch, propertySectionId, showToast],
  );

  const rows = useMemo(() => {
    const list = Array.isArray(payments) ? payments : [];
    return list.map((p: any) => {
      const items: Array<{ name: string; amount: number }> = Array.isArray(
        p.items,
      )
        ? p.items
        : [];
      const totalAmount = items.reduce((sum, i) => sum + (i.amount || 0), 0);
      // チップ表示用に内訳の name 配列を保持 (distinct)。
      const itemNames = Array.from(
        new Set(items.map((i) => String(i.name || "")).filter(Boolean)),
      );
      // 請求年月 "YYYY-MM" → "YYYY年MM月" 表示
      const billingMonth = (() => {
        const m = p.aggregation_month;
        if (!m || !/^\d{4}-\d{2}$/.test(m)) return "-";
        return `${m.slice(0, 4)}年${m.slice(5, 7)}月`;
      })();

      const amountFormatted =
        totalAmount > 0 ? `¥${Number(totalAmount).toLocaleString()}` : "-";
      return {
        id: String(p.payment_id),
        payment_id: p.payment_id,
        sort_key: p.sort_key,
        billing_month: billingMonth,
        title: itemNames,
        status: p.status || "",
        amount: amountFormatted,
        // 入金額: 案C(receipts)未実装のため暫定で status 由来 (入金済=請求額満額 / それ以外="-")
        paid_amount: p.status === "入金済" ? amountFormatted : "-",
        scheduled_date: p.scheduled_date
          ? dayjs(p.scheduled_date).format("YYYY/MM/DD")
          : "-",
        transfer_datetime: p.transfer_datetime
          ? dayjs(p.transfer_datetime).format("YYYY/MM/DD")
          : "-",
        created_at: p.created_at
          ? dayjs(p.created_at).format("YYYY/MM/DD HH:mm")
          : "-",
        remarks: p.remarks || "",
        _raw: p,
      };
    });
  }, [payments]);

  const columns: CustomColumnDef[] = [
    {
      field: "billing_month",
      headerName: "請求年月",
      flex: 1,
      sortable: true,
      renderCell: ({ row, value }) => (
        <Box
          component="span"
          sx={{
            color: "inherit",
            textDecoration: "underline",
            cursor: "pointer",
            fontSize: "14px",
            "&:hover": { color: "#0b9dbd" },
          }}
          onClick={(e) => {
            e.stopPropagation();
            openPaymentDetail(String(row._raw.payment_id));
          }}
        >
          {String(value || "-")}
        </Box>
      ),
    },
    {
      field: "title",
      headerName: "明細項目",
      flex: 1.4,
      sortable: true,
      // 収入一覧/退去後入金履歴と同じグレーチップ (改行なし1行)。
      renderCell: ({ value }) => {
        const names = Array.isArray(value) ? (value as string[]) : [];
        if (names.length === 0) {
          return (
            <Box component="span" sx={{ color: "#999", fontSize: 14 }}>
              -
            </Box>
          );
        }
        return (
          <Box
            sx={{
              display: "flex",
              flexWrap: "nowrap",
              alignItems: "center",
              gap: 0.5,
              py: 0.5,
              minWidth: 0,
              width: "100%",
              overflow: "hidden",
            }}
          >
            {names.map((n, i) => (
              <Box
                key={`${i}-${n}`}
                sx={{
                  bgcolor: "#F0F0F0",
                  display: "inline-flex",
                  alignItems: "center",
                  height: 24,
                  px: 1,
                  borderRadius: "4px",
                  fontSize: 12,
                  color: "#3E3E3E",
                  flexShrink: 0,
                  whiteSpace: "nowrap",
                }}
              >
                {n}
              </Box>
            ))}
          </Box>
        );
      },
    },
    {
      field: "amount",
      headerName: "請求額",
      flex: 1,
      renderCell: (params) => CustomRenderCellWithDash(params),
    },
    {
      field: "paid_amount",
      headerName: "入金額",
      flex: 1,
      renderCell: (params) => CustomRenderCellWithDash(params),
    },
    {
      field: "scheduled_date",
      headerName: "入金期日",
      flex: 1,
      renderCell: (params) => CustomRenderCellWithDash(params),
    },
    {
      field: "transfer_datetime",
      headerName: "入金日",
      flex: 1,
      renderCell: (params) => CustomRenderCellWithDash(params),
    },
    {
      field: "status",
      headerName: "ステータス",
      flex: 1,
      disableTooltip: true,
      renderCell: ({ row }) => (
        <Box sx={{ width: "140px", minWidth: "140px", flexShrink: 0 }}>
          <CustomSelect
            key={`${row.payment_id}_${statusMap[row.payment_id] || row.status}`}
            name={`status_${row.payment_id}`}
            options={statusOptions.map((o) => ({
              label: o.label,
              value: o.value,
            }))}
            width="140px"
            XsWidth="140px"
            fontSize="14px"
            defaultValue={statusMap[row.payment_id] || row.status || ""}
            sx={{
              width: "140px",
              minWidth: "140px",
              flexShrink: 0,
            }}
            onChange={(v) =>
              handleInlineStatusChange(row.payment_id, row.sort_key, String(v))
            }
          />
        </Box>
      ),
    },
    {
      field: "created_at",
      headerName: "登録日時",
      flex: 1.1,
      renderCell: (params) => CustomRenderCellWithDash(params),
    },
  ];

  const handleRowSelection = (ids: string[]) => {
    setSelectedIds(new Set(ids));
  };

  const handleCreate = async (values: PaymentDrawerFormValues) => {
    try {
      // ローカル新規追加された科目 (isNew / category_id 空) を先に BudgetCategory 化して
      // category_id を確定させる (グループと同じく親保存のタイミングで永続化)。
      const items = await Promise.all(
        values.items.map(async (i) => {
          let categoryId = i.category_id;
          if ((i.isNew || !categoryId) && i.name.trim()) {
            categoryId = await persistNewCategory(i.name);
          }
          const amount = i.amount ? parseFloat(i.amount) : 0;
          const taxRate = i.tax_category === "taxable" ? 10 : 0;
          return {
            category_id: categoryId,
            name: i.name,
            amount,
            tax_category: i.tax_category,
            tax_rate: taxRate,
            tax_amount: Math.floor((amount * taxRate) / 100),
          };
        }),
      );

      await dispatch(
        createPayment({
          propertySectionId,
          property_id: propertyId,
          status: values.status,
          items,
          aggregation_month: values.aggregation_month,
          scheduled_date: values.scheduled_date || undefined,
          transfer_datetime: values.transfer_datetime || undefined,
          transfer_method: values.transfer_method || undefined,
          remarks: values.remarks || undefined,
        }),
      ).unwrap();
      showToast({ type: "success", message: "請求を登録しました" });
    } catch {
      showToast({ type: "error", message: "登録に失敗しました" });
      throw new Error("create failed");
    }
  };

  const handleOpenDeleteModal = () => {
    if (selectedIds.size === 0) {
      showToast({
        type: "warning",
        message: "削除する請求管理を選択してください",
      });
      return;
    }
    setDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    setDeleteModalOpen(false);
    setIsDeleting(true);
    try {
      const targets = rows.filter((r) => selectedIds.has(r.id));
      await Promise.all(
        targets.map((r) =>
          dispatch(
            deletePayment({
              propertySectionId,
              paymentId: r.payment_id,
              sort_key: r.sort_key,
            }),
          ).unwrap(),
        ),
      );
      setSelectedIds(new Set());
      showToast({
        type: "success",
        message: `${targets.length}件の請求管理を削除しました`,
      });
    } catch {
      showToast({ type: "error", message: "削除に失敗しました" });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Box>
      <Box>
        <CustomListToolbar
          showSearch={false}
          showFilterActions={true}
          responsiveBreakpoint="md"
          onSearch={handleSearch}
          onClear={handleClear}
          leftExtra={
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1.25}
              sx={{ width: "100%" }}
            >
              <Box sx={{ minWidth: 160 }}>
                <CustomSingleSelect
                  label=""
                  placeholder="ステータス"
                  name="status_filter"
                  control={control}
                  options={statusFilterOptions}
                  labelWidth="0"
                  labelWidthSp="0"
                  inputWidth="100%"
                  inputWidthSp="100%"
                  hasSpacing={false}
                />
              </Box>
              <Box sx={{ minWidth: 160 }}>
                <CustomSingleSelect
                  label=""
                  placeholder="請求項目"
                  name="payment_item_filter"
                  control={control}
                  options={paymentItemFilterOptions}
                  labelWidth="0"
                  labelWidthSp="0"
                  inputWidth="100%"
                  inputWidthSp="100%"
                  hasSpacing={false}
                />
              </Box>
            </Stack>
          }
          perPageOptions={perPageOptions}
          perPageControl={control}
          onPerPageChange={(v) => {
            setPerPage(v);
            setCurrentPage(1);
          }}
          primaryButtonLabel="新規追加"
          primaryButtonOnClick={() => {
            setCreateOpen(true);
          }}
          showPrimaryButton={can("payment:edit")}
          deleteDisabled={isDeleting || selectedIds.size === 0}
          onDeleteClick={handleOpenDeleteModal}
          showDelete={can("payment:delete")}
        />
      </Box>

      <Box sx={{ mt: 1 }}>
        {/* ヘッダーは常時表示し、ローディングはヘッダー下のみで行う
            (物件構成一覧と同じ挙動)。CustomTable の isLoading が body を覆うオーバーレイを出す。
            モバイルは収入一覧(IncomeListTab)と同じく MobileTable でカード表示。 */}
        {isMobile ? (
          <MobileTable
            rows={rows}
            columns={columns.filter((c) => c.field !== "billing_month")}
            isLoading={loading}
            checkBoxOpen
            selectedIds={Array.from(selectedIds)}
            onRowSelection={handleRowSelection}
            hidePagination
            noDataMessage="入金が登録されていません"
            noDataIcon={
              <ReceiptIcon
                sx={{ fontSize: 40, color: colors.border.light, mb: 1 }}
              />
            }
            handlePropertyClick={(row) =>
              openPaymentDetail(String(row._raw.payment_id))
            }
            renderTitle={(row) => (
              <Typography
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  openPaymentDetail(String(row._raw.payment_id));
                }}
                sx={{
                  fontSize: "14px",
                  fontWeight: 500,
                  textDecoration: "underline",
                  cursor: "pointer",
                  color: "#3e3e3e",
                  "&:hover": { color: "#0b9dbd" },
                }}
              >
                {String(row.billing_month || "-")}
              </Typography>
            )}
          />
        ) : (
          <CustomTable
            rows={rows}
            columns={columns}
            enableCheckboxSelection={true}
            onRowSelection={handleRowSelection}
            onPageChange={(page) => setCurrentPage(page)}
            outlined={false}
            minHeight="auto"
            initialSort={{ field: "scheduled_date", direction: "desc" }}
            isLoading={loading}
            noDataMessage="入金が登録されていません"
            noDataIcon={
              <ReceiptIcon
                sx={{ fontSize: 40, color: colors.border.light, mb: 1 }}
              />
            }
          />
        )}
      </Box>

      <PaymentDrawer
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={handleCreate}
        title="請求登録"
        statusOptions={statusOptions}
        sectionPaymentItems={sectionPaymentItems}
        onAddPaymentCategory={handleAddCategory}
        revenueGroupOptions={revenueGroupOptions}
        allowAddPaymentItem
        isEdit={false}
      />

      <PaymentDetailDrawer
        open={Boolean(editPaymentId)}
        onClose={() => setEditPaymentId(null)}
        paymentId={editPaymentId}
        propertySectionId={propertySectionId}
        propertyId={propertyId}
        propertyName={propertyName}
        unitName={unitName}
        onUpdated={() => {
          // 更新後に最新ステータス/集計を再取得 (updatePayment fulfilled で list は
          // ローカル更新されるが、available_statuses 等のメタを再同期するため明示再取得)
          dispatch(
            fetchPaymentsBySection({
              propertySectionId,
              status: appliedFilters.status || undefined,
              payment_item: appliedFilters.payment_item || undefined,
            }),
          );
        }}
      />

      <CustomModal
        title="削除の確認"
        openModal={deleteModalOpen}
        handleCloseModal={() => setDeleteModalOpen(false)}
        bodyText=""
        hideCloseButton
        modalType="deleteModal"
      >
        <Box>
          <Typography sx={{ fontSize: "14px", color: "#666" }}>
            選択した{selectedIds.size}
            件の請求管理を削除してもよろしいでしょうか？
            <br />
            削除されたデータは復元できません。
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
          <CustomButton
            label="戻る"
            onClick={() => setDeleteModalOpen(false)}
            buttonCategory="cancelModal"
          />
          <CustomButton
            label="削除"
            onClick={handleDelete}
            buttonCategory="deleteModal"
            disabled={isDeleting}
          />
        </Box>
      </CustomModal>
    </Box>
  );
};

export default PaymentHistoryTab;
